<template>
  <v-container class="pb-7 pt-4">
    <h1>
      Edit Your Profile
    </h1>

    <v-layout row wrap justify-content-center class="mt-5">
      <v-flex md3 class="pr-5">
        <v-flex xs12 class="text-center px-5">
          <change-profile-picture-modal image-put-endpoint="/api/profile/image" :image="generalInfoForm.profileImageUrl" @refresh="getProfile()" />
        </v-flex>
        <v-flex xs12 class="mt-5 text-center">
          <v-btn type="button" href="/manageaccount/changepassword" color="secondary">
            Change Password
          </v-btn>
          <request-author-access :not-author="notAuthor" class="mt-3" is-signed-in />
        </v-flex>
      </v-flex>
      <v-flex md9>
        <v-tabs v-model="tabs" right>
          <v-tab href="#edit-profile">
            Edit Profile
          </v-tab>
          <v-tab href="#submit-materials">
            Submit Materials
          </v-tab>
        </v-tabs>
        <v-divider class="ma-0 mb-5" />

        <v-tabs-items v-model="tabs">
          <v-tab-item value="edit-profile">
            <form @submit.prevent="updateProfile">
              <v-layout row wrap justify-content-center>
                <v-flex md12 class="text-xs-center">
                  <alert-banner :msg="alertMessage" class="mb-5" />
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                  <v-flex xs12 class="px-4 mb-3">
                      <div class="font-weight-medium headline red--text text--darken-3">
                          General Information
                      </div>
                  </v-flex>
                  <v-flex xs12 sm6 class="px-4">
                      <v-text-field v-model="generalInfoForm.firstName" v-validate="'required'" label="First Name*" name="firstName" :error-messages="errors.collect('firstName')" required />
                  </v-flex>
                  <v-flex xs12 sm6 class="px-4">
                      <v-text-field v-model="generalInfoForm.lastName" v-validate="'required'" label="Last Name*" name="lastName" :error-messages="errors.collect('lastName')" required />
                  </v-flex>
                  <v-flex xs12 sm6 class="px-4">
                      <v-text-field v-model="generalInfoForm.phone" v-validate="'required'" mask="phone" label="Phone*" name="phone" :error-messages="errors.collect('phone')" required />
                  </v-flex>
                  <v-flex xs12 sm6 class="px-4">
                      <v-text-field v-model="generalInfoForm.phoneExtension" label="Phone Extension" name="phoneExtension" />
                  </v-flex>
                  <v-flex xs12 class="px-4">
                      <v-text-field v-model="generalInfoForm.email" v-validate="'required|email'" label="Email*" name="email" :error-messages="errors.collect('email')" type="email" required />
                  </v-flex>

                  <v-autocomplete class="px-4" v-model="generalInfoForm.programIds" box :items="programs" label="Select your Program(s)" item-text="name" item-value="id" multiple chips>
                      <template v-slot:selection="data">
                          <v-chip :selected="data.selected" close class="chip--select-multi" dark @input="generalInfoForm.programIds.splice(data.item, 1)">
                              <v-avatar v-if="data.item.iconUrl">
                                  <img :src="data.item.iconUrl">
                              </v-avatar>
                              {{ data.item.name }}
                          </v-chip>
                      </template>
                  </v-autocomplete>
              </v-layout>
              <v-layout row wrap>
                <v-flex xs12 class="px-4">
                  <div class="font-weight-medium headline red--text text--darken-3 mt-5  mb-3">
                    About Me
                  </div>
                </v-flex>
                <v-flex xs12 class="px-4">
                  <v-select v-model="generalInfoForm.positionTypeId" v-validate="'required'" :items="positions" label="Which best describes your current position?*" name="positionTypeId" :error-messages="errors.collect('positionTypeId')" required item-text="name" item-value="id" />
                </v-flex>
                <v-flex v-if="generalInfoForm.positionTypeId === otherPositionId" xs12 class="px-4">
                  <v-text-field v-model="generalInfoForm.otherPositionTypeDetail" v-validate="'required'" label="Other Position (Please Specify)*" name="otherPositionTypeDetail" :error-messages="errors.collect('otherPositionTypeDetail')" required />
                </v-flex>
                <v-flex xs12 class="px-4 pt-4">
                    <label>Enter a short bio</label>
                    <vue-editor v-model="generalInfoForm.bio" :editorToolbar="customToolbar"></vue-editor>
                </v-flex>
              </v-layout>
              <div class="text-right mt-4">
                <v-btn type="submit" color="secondary" :loading="saving">
                  Update Profile
                </v-btn>
              </div>
            </form>
          </v-tab-item>

          <v-tab-item value="submit-materials">
            <form @submit.prevent="submitCurriculum">
              <v-layout row wrap justify-content-center>
                <v-flex md12 class="text-xs-center">
                  <alert-banner :msg="alertMessage" class="mb-5" />
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex xs12 class="px-4 mb-3">
                  <div class="font-weight-medium headline red--text text--darken-3">
                    Submit Your Own Curriculum
                  </div>
                  <p class="mt-3">
                    Want to submit something simple to the resource library? You can here! If you'd like to submit a full set of course materials, please use the "Request Author Access" button.
                  </p>
                </v-flex>
                <v-flex xs12 md7 class="px-4">
                  <v-text-field v-model="submitCurriculumForm.title" v-validate="'required'" label="Curriculum Title*" name="title" :error-messages="errors.collect('title')" required />
                </v-flex>
                <v-flex xs12 md7 class="px-4">
                  <v-textarea v-model="submitCurriculumForm.description" v-validate="'required'" label="Description*" name="description" :error-messages="errors.collect('description')" />
                </v-flex>

                <v-flex xs12 md7 class="px-4">
                  <v-autocomplete v-model="submitCurriculumForm.gradeLevelIds" box :items="gradeLevels" item-value="id" label="Grade Levels" item-text="name" multiple chips>
                    <template v-slot:selection="data">
                      <v-chip :selected="data.selected" close class="chip--select-multi" dark @input="submitCurriculumForm.gradeLevelIds.splice(data.item, 1)">
                        <v-avatar v-if="data.item.iconUrl">
                          <img :src="data.item.iconUrl">
                        </v-avatar>
                        {{ data.item.name }}
                      </v-chip>
                    </template>
                  </v-autocomplete>
                </v-flex>
                <v-flex xs12 md7 class="px-4">
                  <v-autocomplete v-model="submitCurriculumForm.programIds" box :items="programs" label="Programs" item-text="name" item-value="id" multiple chips>
                    <template v-slot:selection="data">
                      <v-chip :selected="data.selected" close class="chip--select-multi" dark @input="submitCurriculumForm.programIds.splice(data.item, 1)">
                        <v-avatar v-if="data.item.iconUrl">
                          <img :src="data.item.iconUrl">
                        </v-avatar>
                        {{ data.item.name }}
                      </v-chip>
                    </template>
                  </v-autocomplete>
                </v-flex>
                <v-flex xs12 md7 class="px-4">
                  <v-autocomplete v-model="submitCurriculumForm.resourceTypeIds" box :items="resourceTypes" label="Resource Types" item-text="name" item-value="id" multiple chips>
                    <template v-slot:selection="data">
                      <v-chip :selected="data.selected" close class="chip--select-multi" dark @input="submitCurriculumForm.resourceTypeIds.splice(data.item, 1)">
                        <v-avatar v-if="data.item.iconUrl">
                          <img :src="data.item.iconUrl">
                        </v-avatar>
                        {{ data.item.name }}
                      </v-chip>
                    </template>
                  </v-autocomplete>
                </v-flex>

                <v-flex xs12 md7 class="px-4">
                  <v-select v-model="selectedCurriculumSubmitType" :items="curriculumSubmitTypes" label="Item type to submit" />
                </v-flex>
                <v-flex v-if="selectedCurriculumSubmitType == 'Link'" xs12 md7 class="px-4">
                  <v-text-field v-model="submitCurriculumForm.linkUrl" v-validate="'required'" label="Link to Resource*" name="linkUrl" :error-messages="errors.collect('linkUrl')" required />
                  <v-text-field v-model="submitCurriculumForm.linkName" v-validate="'required'" label="Link Display Text*" name="linkUrl" :error-messages="errors.collect('linkName')" required />
                </v-flex>
                <v-flex v-if="selectedCurriculumSubmitType == 'File'" xs12 md7 class="px-4">
                    <p><b>If you have more than one file, please submit them as a zip.</b></p>
                  <v-btn v-if="!submitCurriculumForm.file" class="mx-0" @click="$refs.curriculumFileInput.click()">
                    Upload Curriculum File
                  </v-btn>
                  <input id="curriculumUpload" ref="curriculumFileInput" type="file" name="file" :error-messages="errors.collect('file')" @change="submitCurriculumForm.file = $event.target.files[0]">
                  <v-text-field v-if="submitCurriculumForm.file" v-model="submitCurriculumForm.file.name" append-icon="remove_circle_outline" readonly label="File Ready for Upload" @click:append="fileReset()" />
                </v-flex>
                <v-flex xs12 class="mt-4 mb-2 px-4">
                  All documents added to the resource library should be free of copyright restrictions and able to be distributed for reuse. If the document(s) you are adding are not your original work, please provide proper source citation.
                </v-flex>
                <v-flex xs12 class="px-4">
                  <v-btn type="submit" color="secondary" :loading="submittingCurriculum" class="mx-0">
                    Submit Curriculum
                  </v-btn>
                </v-flex>
              </v-layout>
            </form>
            <v-dialog v-model="showCurriculumSuccessfulModal" max-width="500px">
              <v-card>
                <v-card-title>
                  <span class="headline">
                    Curriculum Submitted Successfully!
                  </span>
                </v-card-title>
                <v-card-text>
                  <p>Your curriculum was successfully submitted and is now under review. Once your curriculum has been reviewed, it will appear in the curriculum library. We will reach out to you if we have any further questions.</p>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn flat @click="showCurriculumSuccessfulModal = false">
                    Close
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-tab-item>
        </v-tabs-items>
      </v-flex>
    </v-layout>
  </v-container>
</template>


<script>
  import axios from 'axios';
  import ChangeProfilePictureModal from '~/Resources/scripts/patterns/ChangeProfilePictureModal.vue';
  import RequestAuthorAccess from './RequestAuthorAccess.vue';

  export default {
    parent: true,

    components: {
      RequestAuthorAccess,
      ChangeProfilePictureModal,
    },

    props: {
      notAuthor: Boolean,
    },

    data: () => ({
      tabs: null,
      profileImageModal: false,
      customToolbar: [
          ["bold", "italic", "underline"],
          [{ list: "ordered" }, { list: "bullet" }]
        ],

      generalInfoForm: {
        firstName: '',
        lastName: '',
        phone: '',
        phoneExtension: '',
        email: '',
        positionTypeId: '',
        bio: '',
        profileImageUrl: '',
        programIds: [],
        otherPositionTypeDetail: '',
      },

      submitCurriculumForm: {
        title: '',
        description: '',
        linkUrl: '',
        linkName: '',
        file: '',
        programIds: '',
        gradeLevelIds: '',
        resourceTypeIds: '',
      },
      selectedCurriculumSubmitType: '',
      curriculumSubmitTypes: ['Link', 'File'],
      programs: [],
      gradeLevels: [],
      resourceTypes: [],
      showCurriculumSuccessfulModal: false,
      profileImageFile: null,
      positions: [],
      submittingCurriculum: false,
      saving: false,
    }),

    computed: {
      otherPositionId() {
        const positions = JSON.parse(JSON.stringify(this.positions));
        const position = positions.find(x => x.name.includes('Other'));
        if (position) return position.id;

        return -1;
      },
    },

    mounted() {
      this.checkURLHashtag();
      this.getProfile();
      this.getPositionTypes();
      this.getPrograms();
      this.getGradeLevels();
      this.getResourceTypes();
    },

    methods: {
      async getPositionTypes() {
        const { data } = await axios.get('/api/registration/position-types');
        this.positions = data;
      },
      getPrograms() {
        axios.get('/api/programs').then((response) => {
          this.programs = response.data;
        });
      },
      getResourceTypes() {
        axios.get('/api/resourcetypes').then((response) => {
          this.resourceTypes = response.data;
        });
      },
      getGradeLevels() {
        axios.get('/api/gradelevels').then((response) => {
          this.gradeLevels = response.data;
        });
      },
      getProfile() {
        axios.get('/api/profile').then((response) => {
          this.generalInfoForm = response.data;
        });
      },

      async updateProfile() {
        this.saving = true;
        try {
          if (this.generalInfoForm.positionTypeId != this.otherPositionId) {
            this.generalInfoForm.otherPositionTypeDetail = '';
          }
          await axios.patch('/api/profile', this.generalInfoForm);
          if (this.profileImageFile) {
            await this.uploadProfileImage();
          }
        } catch (error) {
          this.processErrors(error, this.generalInfoForm);
        } finally {
          this.saving = false;
        }
      },

      async submitCurriculum() {
        if (this.selectedCurriculumSubmitType === 'File') {
          this.submitCurriculum.linkUrl = '';
        }
        this.submittingCurriculum = true;
        try {
          const form = new FormData();
          form.append('File', this.submitCurriculumForm.file);

          const { data } = await axios.post('/api/profile/basic-course', this.submitCurriculumForm);
          if (this.selectedCurriculumSubmitType === 'File') {
            await axios.put(`/api/profile/basic-course/${data.id}/file`, form, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            });
          }
          this.showCurriculumSuccessfulModal = true;
          this.fileReset();
          this.submitCurriculumForm = {
            title: '',
            description: '',
            file: '',
            linkUrl: '',
          };
          this.selectedCurriculumSubmitType = '';
        } catch (error) {
          this.processErrors(error, this.submitCurriculumForm);
        } finally {
          this.submittingCurriculum = false;
        }
      },

      checkURLHashtag() {
        if (window.location.hash) {
          this.tabs = window.location.hash.substr(1);
        }
      },

      fileReset() {
        this.submitCurriculumForm.file = null;
        const input = this.$refs.curriculumFileInput;
        input.type = 'text';
        input.type = 'file';
      },
    },
  };
</script>
<style scoped>
#curriculumUpload {
    position: absolute;
    left: -99999px;
  }
</style>